import type { AuthoritySurfaceId } from "@/config/productNavigation";
import { getAuthoritySurfaceIdForPath } from "@/config/productNavigation";
import {
  AUTH_LOGIN_PATH,
  CONTROLLED_ACCESS_HOLDING_PATH,
} from "@/features/auth/controlledAccessPaths";

export type RouteAuthorityKind = "shared-shell" | "authority-surface" | "platform-console";
export type ShellHeaderMode = "visible" | "hidden";
export type PreferredShellChrome = "adaptive" | "desktop";

export type ShellSurfaceId = AuthoritySurfaceId | "admin";

export type RouteShellPolicy = {
  pathname: string;
  authorityKind: RouteAuthorityKind;
  surfaceId: ShellSurfaceId | null;
  headerMode: ShellHeaderMode;
  showAuthoritySwitcher: boolean;
  showBottomTabs: boolean;
  preferredChrome: PreferredShellChrome;
};

function isAdminConsolePath(pathname: string): boolean {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

function isHomepageCommandSurface(pathname: string): boolean {
  return pathname === "/" || pathname === "/landing-vnext";
}

/** Controlled access surfaces: no product header chrome or bottom tabs. */
function isControlledAccessChromePath(pathname: string): boolean {
  const path = pathname.split("?")[0] ?? pathname;
  if (
    path === CONTROLLED_ACCESS_HOLDING_PATH ||
    path.startsWith(`${CONTROLLED_ACCESS_HOLDING_PATH}/`)
  ) {
    return true;
  }
  return path === AUTH_LOGIN_PATH || path.startsWith(`${AUTH_LOGIN_PATH}/`);
}

/** Mission-control operating surface — no global product nav or marketing chrome. */
function isCommandDeckChromePath(pathname: string): boolean {
  const path = pathname.split("?")[0] ?? pathname;
  return path === "/command-deck" || path.startsWith("/command-deck/");
}

function controlledAccessShellPolicy(pathname: string): RouteShellPolicy {
  return {
    pathname,
    authorityKind: "shared-shell",
    surfaceId: null,
    headerMode: "hidden",
    showAuthoritySwitcher: false,
    showBottomTabs: false,
    preferredChrome: "adaptive",
  };
}

export function resolveRouteShellPolicy(pathname: string): RouteShellPolicy {
  if (isHomepageCommandSurface(pathname)) {
    return {
      pathname,
      authorityKind: "shared-shell",
      surfaceId: null,
      headerMode: "hidden",
      showAuthoritySwitcher: false,
      showBottomTabs: false,
      preferredChrome: "adaptive",
    };
  }

  if (isControlledAccessChromePath(pathname)) {
    return controlledAccessShellPolicy(pathname);
  }

  if (isCommandDeckChromePath(pathname)) {
    return {
      pathname,
      authorityKind: "authority-surface",
      surfaceId: "command_deck",
      headerMode: "hidden",
      showAuthoritySwitcher: true,
      showBottomTabs: false,
      preferredChrome: "desktop",
    };
  }

  if (pathname === "/guided/start-from-world") {
    return {
      pathname,
      authorityKind: "authority-surface",
      surfaceId: "guided_world_forge",
      headerMode: "hidden",
      showAuthoritySwitcher: true,
      showBottomTabs: false,
      preferredChrome: "desktop",
    };
  }

  if (pathname.startsWith("/guided-studio")) {
    return {
      pathname,
      authorityKind: "authority-surface",
      surfaceId: "guided_studio",
      headerMode: "hidden",
      showAuthoritySwitcher: true,
      showBottomTabs: false,
      preferredChrome: "desktop",
    };
  }

  if (pathname.startsWith("/wearables/connect")) {
    return {
      pathname,
      authorityKind: "authority-surface",
      surfaceId: "wearables_hub",
      headerMode: "hidden",
      showAuthoritySwitcher: true,
      showBottomTabs: false,
      preferredChrome: "desktop",
    };
  }

  if (pathname.startsWith("/wearables/start")) {
    return {
      pathname,
      authorityKind: "authority-surface",
      surfaceId: "wearables_hub",
      headerMode: "hidden",
      showAuthoritySwitcher: true,
      showBottomTabs: false,
      preferredChrome: "desktop",
    };
  }

  if (pathname.startsWith("/onboarding/wearables")) {
    return {
      pathname,
      authorityKind: "authority-surface",
      surfaceId: "wearables_hub",
      headerMode: "hidden",
      showAuthoritySwitcher: true,
      showBottomTabs: false,
      preferredChrome: "desktop",
    };
  }

  const authoritySurfaceId = getAuthoritySurfaceIdForPath(pathname);
  if (authoritySurfaceId) {
    return {
      pathname,
      authorityKind: "authority-surface",
      surfaceId: authoritySurfaceId,
      headerMode: "hidden",
      showAuthoritySwitcher: true,
      showBottomTabs: false,
      preferredChrome: authoritySurfaceId === "omnicine" ? "desktop" : "adaptive",
    };
  }

  if (isAdminConsolePath(pathname)) {
    return {
      pathname,
      authorityKind: "platform-console",
      surfaceId: "admin",
      headerMode: "visible",
      showAuthoritySwitcher: false,
      showBottomTabs: false,
      preferredChrome: "desktop",
    };
  }

  return {
    pathname,
    authorityKind: "shared-shell",
    surfaceId: null,
    headerMode: "visible",
    showAuthoritySwitcher: false,
    showBottomTabs: true,
    preferredChrome: "adaptive",
  };
}

export function shouldForceDesktopShell(pathname: string): boolean {
  if (pathname.startsWith("/wearables/start")) {
    return true;
  }

  if (pathname.startsWith("/onboarding/wearables")) {
    return true;
  }

  if (pathname.startsWith("/wearables")) {
    return true;
  }

  return resolveRouteShellPolicy(pathname).preferredChrome === "desktop";
}

export function usesAuthorityChrome(pathname: string): boolean {
  return resolveRouteShellPolicy(pathname).authorityKind === "authority-surface";
}